import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { hasFieldProperties } from '../../../parser/ConfigurationItemTypes';
import {
  selectRelatedObjectConfigurationTypeFilterOptions,
  selectRelatedObjectFilterOptions,
  selectSingleObject,
  selectSingleObjectActiveTab,
} from '../../../../reducers/documentation/salesforceDocumentationReducer';
import { selectCrmOrgAlerts } from '../../../../reducers/global/alertsReducers';
import { selectAssignments } from '../../../../reducers/global/assignmentReducers';
import { selectCrmOrgAutomations } from '../../../../reducers/global/automationReducers';
import { selectDedup, selectMatching } from '../../../../reducers/global/dedupMatchingReducers';
import { DocumentationTabTypes } from '../../../../types/enums/DocumentationTabTypes';
import {
  getFieldsTabFilters,
  getApexTabFilters,
  getSalesforceAutomationsTabFilters,
  getValidationRulesTabFilters,
  getLayoutsTabFilters,
  getRecordTypesTabFilters,
  getRelatedObjectsFilters,
  getDocumentationAutomationFilters,
  dedupMatchingFilters,
} from './filtersOptions';
import { selectScheduledAssignments } from '../../../../reducers/global/scheduledAssignmentReducers';
import { getDeployedVersions } from './utils';
import { selectParsedFields } from '../../../../reducers/global/parserReducer';
import { useDocumentationContext } from '../../context/DocumentationContext';
import { useDocumentationFiltersContext } from './DocumentationFiltersContext';
import { RecordTypeFilterItem, StepFilterItem, DocumentationFiltersTypesMap } from './filterTypes';
import { createStepId } from '../../pills/pillsUtils';

/**
 * Hook that generates filter definitions based on current documentation context
 * Moved from DocumentationFiltersContext to keep context only for state management
 */
export const useDocumentationFilters = (crmOrgId: string) => {
  const { recordTypesUsedInCanvasByObjectName } = useDocumentationContext();
  const { filterValues, setRecordTypesItems } = useDocumentationFiltersContext();
  const objectTypeName = useSelector(selectSingleObject);
  const activeTab = useSelector(selectSingleObjectActiveTab);
  const fieldsWithUtilization = useSelector(selectParsedFields(crmOrgId));
  const automations = useSelector(selectCrmOrgAutomations(crmOrgId));
  const alerts = useSelector(selectCrmOrgAlerts(crmOrgId));
  const dedup = useSelector(selectDedup);
  const matching = useSelector(selectMatching);
  const assignments = useSelector(selectAssignments);
  const scheduledAssignments = useSelector(selectScheduledAssignments);
  const relatedObjectsOptions = useSelector(selectRelatedObjectFilterOptions);
  const relatedObjectConfigurationTypeFilterOptions = useSelector(
    selectRelatedObjectConfigurationTypeFilterOptions,
  );

  const deployedAutomations = useMemo(
    () => ({
      automations: getDeployedVersions(automations),
      alerts: getDeployedVersions(alerts),
      matching: getDeployedVersions(matching),
      dedup: getDeployedVersions(dedup),
      assignments: getDeployedVersions(assignments),
      scheduledAssignments: getDeployedVersions(scheduledAssignments),
    }),
    [automations, alerts, matching, dedup, assignments, scheduledAssignments],
  );

  const recordTypesItems = useMemo((): RecordTypeFilterItem[] => {
    const recordTypesForObject =
      recordTypesUsedInCanvasByObjectName?.[objectTypeName?.objectType ?? ''];

    if (!recordTypesForObject) return [];

    return Object.entries(recordTypesForObject).map(
      ([recordTypeId, { label, steps, leadingFieldName }]) => ({
        value: recordTypeId,
        label,
        steps: steps.map((step) => ({
          ...step,
          value: createStepId({
            objectName: objectTypeName?.objectType ?? '',
            stepFullName: step.label,
            leadingField: leadingFieldName,
          }),
          recordTypeId,
        })),
      }),
    );
  }, [recordTypesUsedInCanvasByObjectName, objectTypeName?.objectType]);

  useEffect(() => {
    setRecordTypesItems(recordTypesItems);
  }, [recordTypesItems, setRecordTypesItems]);

  const allStepsItems = useMemo((): StepFilterItem[] => {
    return recordTypesItems.flatMap((recordType) => recordType.steps);
  }, [recordTypesItems]);

  const selectedRecordTypesValues = useMemo(
    () => filterValues?.recordTypes?.selectedValues ?? [],
    [filterValues],
  );

  const stepsItems = useMemo((): StepFilterItem[] => {
    if (selectedRecordTypesValues.length === 0) {
      return allStepsItems;
    }
    return allStepsItems.filter((step) => selectedRecordTypesValues.includes(step.recordTypeId));
  }, [allStepsItems, selectedRecordTypesValues]);

  const baseFilters = useMemo(() => {
    if (!activeTab) return {};

    switch (activeTab) {
      case DocumentationTabTypes.FIELDS: {
        const fields = (fieldsWithUtilization?.[objectTypeName?.objectType as any] ?? []).filter(
          hasFieldProperties,
        );
        return getFieldsTabFilters(fields);
      }

      case DocumentationTabTypes.APEX:
        return getApexTabFilters(recordTypesItems, stepsItems);

      case DocumentationTabTypes.LAYOUTS:
        return getLayoutsTabFilters(recordTypesItems);

      case DocumentationTabTypes.SF_AUTOMATIONS:
        return getSalesforceAutomationsTabFilters(recordTypesItems, stepsItems);

      case DocumentationTabTypes.VALIDATION_RULES:
        return getValidationRulesTabFilters(recordTypesItems, stepsItems);

      case DocumentationTabTypes.SWEEP_AUTOMATIONS:
        return getDocumentationAutomationFilters(
          deployedAutomations.automations,
          objectTypeName?.objectType ?? '',
          recordTypesItems,
        );

      case DocumentationTabTypes.PLAYBOOK_ALERTS:
        return getDocumentationAutomationFilters(
          deployedAutomations.alerts,
          objectTypeName?.objectType ?? '',
          recordTypesItems,
        );

      case DocumentationTabTypes.ASSIGNMENTS:
      case DocumentationTabTypes.SCHEDULED_ASSIGNMENTS:
        return getDocumentationAutomationFilters(
          deployedAutomations.alerts,
          objectTypeName?.objectType ?? '',
          recordTypesItems,
        );

      case DocumentationTabTypes.MATCHING_DEDUPE:
        return dedupMatchingFilters({
          recordTypesItems,
        });

      case DocumentationTabTypes.RECORD_TYPES:
        return getRecordTypesTabFilters();

      case DocumentationTabTypes.RELATED_OBJECTS:
        return getRelatedObjectsFilters(
          relatedObjectsOptions?.filter((obj) => obj.value !== objectTypeName?.objectType) ?? [],
          relatedObjectConfigurationTypeFilterOptions ?? [],
        );

      default:
        return {} as any;
    }
  }, [
    activeTab,
    fieldsWithUtilization,
    objectTypeName?.objectType,
    deployedAutomations,
    relatedObjectsOptions,
    relatedObjectConfigurationTypeFilterOptions,
    recordTypesItems,
    stepsItems,
  ]);

  /**
   * Filter out Record Types and Step filters if canvasFunnelMap is not provided
   */
  const filters = useMemo(() => {
    if (!recordTypesUsedInCanvasByObjectName) {
      const filteredFilters = { ...baseFilters };

      if (filteredFilters[DocumentationFiltersTypesMap.recordTypes]) {
        delete filteredFilters[DocumentationFiltersTypesMap.recordTypes];
      }

      if (filteredFilters[DocumentationFiltersTypesMap.steps]) {
        delete filteredFilters[DocumentationFiltersTypesMap.steps];
      }

      return filteredFilters;
    }

    return baseFilters;
  }, [baseFilters, recordTypesUsedInCanvasByObjectName]);

  const automationsList = useMemo(() => {
    if (!activeTab) return [];

    switch (activeTab) {
      case DocumentationTabTypes.SWEEP_AUTOMATIONS:
        return deployedAutomations.automations;
      case DocumentationTabTypes.PLAYBOOK_ALERTS:
        return deployedAutomations.alerts;
      case DocumentationTabTypes.MATCHING_DEDUPE:
        return [...deployedAutomations.matching, ...deployedAutomations.dedup];
      case DocumentationTabTypes.ASSIGNMENTS:
        return deployedAutomations.assignments;
      case DocumentationTabTypes.SCHEDULED_ASSIGNMENTS:
        return deployedAutomations.scheduledAssignments;
      default:
        return [];
    }
  }, [activeTab, deployedAutomations]);

  return {
    filters,
    automations: automationsList,
    crmOrgId,
    recordTypesUsedInCanvasByObjectName,
  };
};
